import { HTTP_STATUS } from '../domain/domainCodes.js'
/**
 * File uploads, and the one legacy coupling that cannot be adapted away in a service
 * module (W2).
 *
 * Still `XMLHttpRequest` rather than `fetch`: not for progress — the bars are gone, see
 * `FileField` — but because these endpoints answer an HTML callback that has to be read
 * off `responseText`, and because a send has to be abortable when the sheet closes.
 *
 * THE IFRAME CALLBACK. `Users/CreateMultiUsers` answers `text/html` — and so do
 * `CategoriesManagement/save` and `phishing-templates/import`, each naming a different
 * callback function:
 *
 *     <html><body><script>window.parent.finishUpload(true,'12 users added');</script></body></html>
 *
 * That is jQuery-era plumbing: the form posted into a hidden iframe, and the response
 * called back into the parent frame because there was no other way to return a result.
 * The endpoint is not changed to suit us (R2) — the reply is parsed instead, which is
 * both safer than the original (nothing is executed) and invisible to the screen.
 */

import { ApiError } from './api'
import { transport } from '../components/strings'

/** `finishUpload(true,'…')` / `finishUpload(false,"…")`, single or double quoted. */
const FINISH_UPLOAD = /finishUpload\(\s*(true|false)\s*,\s*(['"])([\s\S]*?)\2\s*\)/i

/**
 * The same trick, a different function and five arguments - `CategoriesManagement/save`
 * answers `finishSavingCategory(ok, 'message', 'id', 'name', publicity)`.
 *
 * The ID is the part that matters. Saving is the only way to learn the id a NEW topic was
 * given, and the wizard needs it: the chapters and questions were written against a topic
 * that had none, and without it a second save would create a second topic.
 */
const FINISH_CATEGORY =
  /finishSavingCategory\(\s*(true|false)\s*,\s*'([\s\S]*?)'\s*(?:,\s*'([\s\S]*?)'\s*)?(?:,\s*'([\s\S]*?)'\s*)?(?:,\s*(-?\d+)\s*)?\)/i

/**
 * A third spelling of the same convention - `phishing-templates/import` answers
 * `parent.finishImport(ok, 'message')`. Same two arguments as `finishUpload`, different
 * function name, so it needs its own pattern rather than a looser one: matching any
 * `finish*(` would make the parser guess at markup it does not understand.
 */
const FINISH_IMPORT = /finishImport\(\s*(true|false)\s*,\s*(['"])([\s\S]*?)\2\s*\)/i

export function parseImportCallback(text) {
  const match = FINISH_IMPORT.exec(text ?? '')
  if (!match) return null

  return {
    ok: match[1].toLowerCase() === 'true',
    message: match[3].replace(/<br\s*\/?>/gi, String.fromCharCode(10)).trim(),
  }
}

export function parseSaveCategoryCallback(text) {
  const match = FINISH_CATEGORY.exec(text ?? '')
  if (!match) return null

  return {
    ok: match[1].toLowerCase() === 'true',
    message: (match[2] ?? '').replace(/<br\s*\/?>/gi, String.fromCharCode(10)).trim(),
    id: match[3] ? Number(match[3]) : null,
    name: match[4] ?? '',
    publicity: match[5] != null ? Number(match[5]) : null,
  }
}

/**
 * Reads the HTML callback, if that is what came back.
 * @returns {{ ok: boolean, message: string }|null} null when it is not one
 */

export function parseUploadCallback(text) {
  const match = FINISH_UPLOAD.exec(text ?? '')
  if (!match) return null

  return {
    ok: match[1].toLowerCase() === 'true',
    // The server joins multi-line reports with <br/>; the message is displayed as
    // received (W6), so only the separator is normalised.
    message: match[3].replace(/<br\s*\/?>/gi, '\n').trim(),
  }
}

/**
 * POSTs a FormData body and reports progress.
 *
 * @param {string} url
 * @param {FormData} formData
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<any>} parsed JSON, or `{ Result, Message }` from an HTML callback
 */
export function upload(url, formData, { signal } = {}) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', url, true)
    xhr.withCredentials = true
    xhr.setRequestHeader('Accept', 'application/json, text/html')

    const abort = () => xhr.abort()
    signal?.addEventListener('abort', abort, { once: true })

    const done = () => signal?.removeEventListener('abort', abort)

    xhr.onerror = () => {
      done()
      reject(new ApiError(transport.networkUpload, { code: 'NETWORK' }))
    }

    xhr.onabort = () => {
      done()
      reject(Object.assign(new Error('Upload cancelled'), { name: 'AbortError' }))
    }

    xhr.onload = () => {
      done()

      const contentType = xhr.getResponseHeader('Content-Type') ?? ''
      const text = xhr.responseText ?? ''

      if (xhr.status === HTTP_STATUS.UNAUTHORIZED) {
        reject(new ApiError(transport.sessionEnded, { status: HTTP_STATUS.UNAUTHORIZED, code: 'SESSION' }))
        return
      }

      if (xhr.status === HTTP_STATUS.FORBIDDEN) {
        reject(new ApiError(transport.denied, {
          status: HTTP_STATUS.FORBIDDEN, code: 'PROBLEM', denied: true,
        }))
        return
      }

      // The iframe callback. Checked before the status, because the legacy endpoint
      // reports FAILURE with a 200 and `finishUpload(false, …)`.
      const callback = parseUploadCallback(text)
      if (callback) {
        if (callback.ok) resolve({ Result: true, Message: callback.message })
        else reject(new ApiError(callback.message, { status: xhr.status, code: 'RESULT' }))
        return
      }

      /* The phishing importer's variant. Same two arguments, so it reduces to the same
         `{ Result, Message }` the JSON endpoints answer with. */
      const imported = parseImportCallback(text)
      if (imported) {
        if (imported.ok) resolve({ Result: true, Message: imported.message })
        else reject(new ApiError(imported.message, { status: xhr.status, code: 'RESULT' }))
        return
      }

      /* The category wizard's variant of the same convention. It is resolved WHOLE rather
         than reduced to `{ Result, Message }`, because the id it carries is the only way a
         newly created topic learns its own. A failure still throws like any other. */
      const saved = parseSaveCategoryCallback(text)
      if (saved) {
        if (saved.ok) resolve({ Result: true, Message: saved.message, ...saved })
        else reject(new ApiError(saved.message, { status: xhr.status, code: 'RESULT' }))
        return
      }

      if (contentType.includes('json')) {
        const data = safeJson(text)
        if (xhr.status >= 400) {
          reject(new ApiError(problemDetail(data) ?? `Upload failed (${xhr.status})`, {
            status: xhr.status, code: 'PROBLEM', data,
          }))
          return
        }
        if (data?.Result === false) {
          reject(new ApiError(data.Message || 'Upload failed', { status: HTTP_STATUS.OK, code: 'RESULT', data }))
          return
        }
        resolve(data)
        return
      }

      if (xhr.status >= 400) {
        reject(new ApiError(transport.uploadFailed(xhr.status), { status: xhr.status, code: 'HTTP' }))
        return
      }

      // HTML that is not the callback is a page — the login screen, an error page.
      if (contentType.includes('html')) {
        reject(new ApiError(transport.html, {
          status: xhr.status, code: 'HTML',
        }))
        return
      }

      resolve(text)
    }

    xhr.send(formData)
  })
}

function safeJson(text) {
  try {
    return JSON.parse(text)
  } catch {
    return null
  }
}

function problemDetail(data) {
  if (!data || typeof data !== 'object') return null
  return data.detail || data.Detail || data.title || data.Title || data.Message || null
}
